const mgL = 'mg/L'
const molL = 'mol/L'
const molKg = 'mol/kg'
const mmHg = 'mmHg'
const log10MgL = 'Log10 (mg/L)'
const log10MolL = 'Log10 (mol/L)'
const log10MmHg = 'Log10 (mmHg)'
const minusLog10MgL = '-Log10 (mg/L)'
const minusLog10MolL = '-Log10 (mol/L)'
const minusLog10MgKg = '-Log10 (mg/kg)'
const minusLog10MolKg = '-Log10 (mol/kg)'
const lKg = 'L/kg'
const mgKg = 'mg/kg'
const cP = 'cP'
const log10Cp = 'Log10 (cP)'
const log10LKg = 'Log10 (L/kg)'
const log10AtmM3Mol = 'Log10 (atm-m³/mol)'
const log10Cm3MoleculeSec = 'Log10 (cm³/molecule-sec)'
const log10MgKg = 'Log10 (mg/kg)'
const pKaDisplay = 'pKa'
const ulMinCellsDisplay = 'uL/min/10^6 cells'
const days = 'days'
const log10Days = 'Log10 (days)'

const normalizeUnitKey = (unit) => (typeof unit === 'string' ? unit.trim().toLowerCase() : null)

const unitAliases = [
  { canonical: 'mg/cm²-event', aliases: ['mg/cm2-event', 'mg/cm²-event'] },
  { canonical: '°C', aliases: ['°c', 'deg c', 'celsius'] },
  { canonical: '°K', aliases: ['kelvin'] },
  { canonical: 'Pa-m³/mol', aliases: ['pa-m3/mol', 'pa-m³/mol'] },
  { canonical: 'days', aliases: ['days'] },
  { canonical: 'minutes', aliases: ['minutes'] },
  { canonical: mgL, aliases: ['mg/l'] },
  { canonical: mmHg, aliases: ['mmhg'] },
  { canonical: molL, aliases: ['mol/l', 'moles/l'] },
  { canonical: molKg, aliases: ['mol/kg'] },
  { canonical: 'atm-m³/mol', aliases: ['atm-m3/mol', 'atm-m³/mol', 'atm-m3/mole'] },
  { canonical: 'hours', aliases: ['hours'] },
  { canonical: mgKg, aliases: ['mg/kg'] },
  { canonical: lKg, aliases: ['l/kg'] },
  { canonical: 'g/cm³', aliases: ['g/cm³', 'g/cm3'] },
  { canonical: 'dyn/cm', aliases: ['dyn/cm'] },
  { canonical: cP, aliases: ['cp'] },
  { canonical: 'mW/mK', aliases: ['mw/mk'] },
  { canonical: 'Log10 (unitless)', aliases: ['log10', 'log10 unitless'] },
  { canonical: log10MmHg, aliases: ['log10 mmhg', 'log10(mmhg)', 'log10 (mmhg)'] },
  { canonical: log10MolL, aliases: ['log10 moles/l', 'log10 mol/l', 'log10(mol/l)', 'log10 (mol/l)'] },
  { canonical: log10MgL, aliases: ['log10 (mg/l)', 'log10(mg/l)'] },
  { canonical: log10AtmM3Mol, aliases: ['log10 atm-m3/mole', 'log10 atm-m3/mol'] },
  { canonical: log10Cm3MoleculeSec, aliases: ['log 10 cm3/molecule-sec'] },
  { canonical: log10MgKg, aliases: ['log10 mg/kg'] },
  { canonical: pKaDisplay, aliases: ['pka ???'] },
  { canonical: ulMinCellsDisplay, aliases: ['ul/min/10^6 cells'] },
  { canonical: minusLog10MgL, aliases: ['-log10(mg/l)', '-log10 (mg/l)'] },
  { canonical: minusLog10MolL, aliases: ['-log10(mol/l)', '-log10 (mol/l)'] },
  { canonical: minusLog10MgKg, aliases: ['-log10(mg/kg)', '-log10 (mg/kg)'] },
  { canonical: minusLog10MolKg, aliases: ['-log10(mol/kg)', '-log10 (mol/kg)'] },
  { canonical: log10Cp, aliases: ['log10 (cp)'] },
  { canonical: log10LKg, aliases: ['log10 (l/kg)', 'Log10 L/kg'] },
  { canonical: days, aliases: ['days'] },
  { canonical: log10Days, aliases: ['log10 (days)', 'log10 days'] },
]

const buildUnitMap = (aliasGroups) =>
  aliasGroups.reduce((map, { canonical, aliases }) => {
    aliases.forEach((alias) => {
      const normalizedAlias = normalizeUnitKey(alias)

      if (normalizedAlias) {
        map[normalizedAlias] = canonical
      }
    })

    return map
  }, {})

const unitMap = buildUnitMap(unitAliases)

const logPairs = [
  { nonLog: mmHg, log: log10MmHg },
  { nonLog: cP, log: log10Cp },
  { nonLog: lKg, log: log10LKg },
  { nonLog: days, log: log10Days },
]

const minusLogPairs = [
  { nonLog: mgL, log: minusLog10MgL },
  { nonLog: molL, log: minusLog10MolL },
  { nonLog: mgKg, log: minusLog10MgKg },
  { nonLog: molKg, log: minusLog10MolKg },
]

const logConversions = logPairs.map(({ nonLog, log }) => ({
  unit1: nonLog,
  unit2: log,
  requiresMW: false,
  convertUnit1ToUnit2: (value) => (value != null ? Math.log10(value) : null),
  convertUnit2ToUnit1: (value) => (value != null ? Math.pow(10, value) : null),
}))

const minusLogConversions = minusLogPairs.map(({ nonLog, log }) => ({
  unit1: nonLog,
  unit2: log,
  requiresMW: false,
  convertUnit1ToUnit2: (value) => (value != null ? -Math.log10(value) : null),
  convertUnit2ToUnit1: (value) => (value != null ? Math.pow(10, -value) : null),
}))

const concentrationConversions = [
  {
    unit1: mgL,
    unit2: molL,
    requiresMW: true,
    convertUnit1ToUnit2: (mgL, mass) => mgL / (1000 * mass),
    convertUnit2ToUnit1: (molL, mass) => molL * mass * 1000,
  },
  {
    unit1: mgL,
    unit2: minusLog10MolL,
    requiresMW: true,
    convertUnit1ToUnit2: (mgL, mass) => -Math.log10(mgL / (1000 * mass)),
    convertUnit2ToUnit1: (minusLog10MolL, mass) => Math.pow(10, -minusLog10MolL) * mass * 1000,
  },
  {
    unit1: molL,
    unit2: minusLog10MgL,
    requiresMW: true,
    convertUnit1ToUnit2: (molL, mass) => -Math.log10(molL * mass * 1000),
    convertUnit2ToUnit1: (minusLog10MgL, mass) => Math.pow(10, -minusLog10MgL) / (1000 * mass),
  },
  {
    unit1: minusLog10MgL,
    unit2: minusLog10MolL,
    requiresMW: true,
    convertUnit1ToUnit2: (minusLog10MgL, mass) => minusLog10MgL - Math.log10(1000 * mass),
    convertUnit2ToUnit1: (minusLog10MolL, mass) => minusLog10MolL + Math.log10(1000 * mass),
  },
  {
    unit1: mgKg,
    unit2: molKg,
    requiresMW: true,
    convertUnit1ToUnit2: (mgKg, mass) => mgKg / (1000 * mass),
    convertUnit2ToUnit1: (molKg, mass) => molKg * mass * 1000,
  },
  {
    unit1: mgKg,
    unit2: minusLog10MolKg,
    requiresMW: true,
    convertUnit1ToUnit2: (mgKg, mass) => -Math.log10(mgKg / (1000 * mass)),
    convertUnit2ToUnit1: (minusLog10MolKg, mass) => Math.pow(10, -minusLog10MolKg) * mass * 1000,
  },
  {
    unit1: molKg,
    unit2: minusLog10MgKg,
    requiresMW: true,
    convertUnit1ToUnit2: (molKg, mass) => -Math.log10(molKg * mass * 1000),
    convertUnit2ToUnit1: (minusLog10MgKg, mass) => Math.pow(10, -minusLog10MgKg) / (1000 * mass),
  },
  {
    unit1: minusLog10MgKg,
    unit2: minusLog10MolKg,
    requiresMW: true,
    convertUnit1ToUnit2: (minusLog10MgKg, mass) => minusLog10MgKg - Math.log10(1000 * mass),
    convertUnit2ToUnit1: (minusLog10MolKg, mass) => minusLog10MolKg + Math.log10(1000 * mass),
  },
]

const unitConversions = [...logConversions, ...minusLogConversions, ...concentrationConversions]

const mapUnitOrOriginal = (unit) => {
  const normalizedUnit = normalizeUnitKey(unit)

  if (!normalizedUnit) return unit

  return unitMap[normalizedUnit] ?? unit
}

const getPossibleConversionsForUnit = (unit) =>
  unitConversions
    .filter((conversion) => conversion.unit1 === unit || conversion.unit2 === unit)
    .map((conversion) => (conversion.unit1 === unit ? conversion?.unit2 : conversion?.unit1))

const findConversionForUnits = (fromUnit, toUnit) =>
  unitConversions.find(
    (conversion) =>
      (conversion.unit1 === fromUnit && conversion.unit2 === toUnit) ||
      (conversion.unit2 === fromUnit && conversion.unit1 === toUnit),
  )

const convertValueToUnit = ({ value, fromUnit, toUnit, mass }) => {
  const conversion = findConversionForUnits(fromUnit, toUnit)
  if (!conversion) return value
  if (conversion.requiresMW && mass == null) return value
  return conversion.unit1 === fromUnit
    ? conversion.convertUnit1ToUnit2(value, mass)
    : conversion.convertUnit2ToUnit1(value, mass)
}

export { getPossibleConversionsForUnit, mapUnitOrOriginal, convertValueToUnit }
